import { type FC, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibChevronDownIcon } from '@chewy/kib-icons-react'
import { KibTooltip } from '@chewy/kib-tooltip-react'
import type { Instance } from '@popperjs/core'
import { createPopper } from '@popperjs/core'

import type { RhapInlineSelectOption } from '../common/RhapInlineSelect'
import ChatHistoryDropdownMenu from './ChatHistoryDropdownMenu'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import '@chewy/kib-tooltip-styles/dist/kib-tooltip.css'
import styles from './ChatHistoryDropdown.module.scss'

const PORTAL_Z_INDEX = 1500

export type ChatHistoryOption = RhapInlineSelectOption & {
  lastUpdateTime?: number
}

export type ChatHistorySectionKey =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'previous'

export interface ChatHistoryDropdownProps {
  /** Accessible label for the trigger button */
  ariaLabel?: string
  disabled?: boolean
  /** Called when user selects an option; close dropdown after selection */
  onSelect: (value: string) => void
  /** Options to show in the dropdown; lastUpdateTime used for grouping and timestamp */
  options: ChatHistoryOption[]
  /** Optional class for the trigger button (e.g. to match NEW_CHAT button style) */
  triggerClassName?: string
  /** Button label when provided (e.g. i18n "Past chats"); otherwise shows selected option label */
  triggerLabel?: string
  /** Render dropdown in a portal (document.body) to avoid clipping; default true */
  usePortal?: boolean
  /** Currently selected value (option.value) */
  value: string
}

const ChatHistoryDropdown: FC<ChatHistoryDropdownProps> = ({
  options,
  value,
  onSelect,
  disabled = false,
  ariaLabel,
  triggerClassName,
  triggerLabel: triggerLabelProp,
  usePortal = true,
}) => {
  const { t } = useTranslation('Agent')
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  )
  const [nonPortalMenuEl, setNonPortalMenuEl] = useState<HTMLDivElement | null>(
    null,
  )
  const popperInstanceRef = useRef<Instance | null>(null)

  const selectedOption = options.find((opt) => opt.value === value)
  const triggerLabel =
    triggerLabelProp ??
    selectedOption?.label ??
    options[0]?.label ??
    t('NO_CHAT_HISTORY')

  const hasSelectableOptions = options.some((opt) => opt.value !== '')

  const handleTriggerClick = () => {
    setIsOpen((prev) => !prev)
  }

  useEffect(() => {
    if (!isOpen) return undefined

    const menuElement = usePortal ? popperElement : nonPortalMenuEl
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const inTrigger = triggerRef.current?.contains(target) ?? false
      const inMenu = menuElement?.contains(target) ?? false
      if (!inTrigger && !inMenu) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [usePortal, isOpen, popperElement, nonPortalMenuEl])

  useEffect(() => {
    if (!usePortal || !triggerRef.current || !popperElement || !isOpen) {
      if (popperInstanceRef.current) {
        popperInstanceRef.current.destroy()
        popperInstanceRef.current = null
      }
      return undefined
    }

    popperInstanceRef.current = createPopper(
      triggerRef.current,
      popperElement,
      {
        placement: 'bottom',
        modifiers: [
          { name: 'offset', options: { offset: [0, 8] } },
          {
            name: 'preventOverflow',
            options: { boundary: 'viewport', padding: 8 },
          },
          {
            name: 'flip',
            options: { fallbackPlacements: ['top'] },
          },
        ],
      },
    )

    return () => {
      if (popperInstanceRef.current) {
        popperInstanceRef.current.destroy()
        popperInstanceRef.current = null
      }
    }
  }, [usePortal, isOpen, popperElement])

  const menuContent = (
    <ChatHistoryDropdownMenu
      options={options}
      value={value}
      onClose={() => setIsOpen(false)}
      onSelect={onSelect}
    />
  )

  return (
    <>
      <KibButtonNew
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        className={triggerClassName}
        data-testid="chat-history-dropdown-trigger"
        disabled={disabled || !hasSelectableOptions}
        emphasis="tertiary"
        icon={<KibChevronDownIcon />}
        iconPosition="end"
        ref={triggerRef}
        size="small"
        theme="utility"
        title={triggerLabel}
        onClick={handleTriggerClick}
      >
        {triggerLabel}
      </KibButtonNew>
      {usePortal ? (
        isOpen &&
        createPortal(
          <div
            className={styles.dropdown}
            ref={setPopperElement}
            style={{ zIndex: PORTAL_Z_INDEX }}
          >
            <div className={styles.dropdownScroll}>{menuContent}</div>
          </div>,
          document.body,
        )
      ) : (
        <KibTooltip
          className={styles.dropdown}
          placement="bottom"
          popperModifiers={[
            { name: 'offset', options: { offset: [0, 8] } },
            { name: 'flip', enabled: true },
          ]}
          trigger={triggerRef}
        >
          {isOpen ? (
            <div className={styles.dropdownScroll} ref={setNonPortalMenuEl}>
              {menuContent}
            </div>
          ) : null}
        </KibTooltip>
      )}
    </>
  )
}

export default ChatHistoryDropdown
